import { type JSX, useState } from 'react';
import { redirectToBeastModeEscalation } from '@gonfalon/beastmode/redirectToBeastModeEscalation';
import { type BeastModePermission, shouldRefreshBeastMode } from '@gonfalon/beastmode/shouldRefreshBeastMode';

import type { ConfirmationProps } from './Confirmation';
import { Confirmation } from './Confirmation';

export type WithConfirmationProps = {
  render: ({ onAction, disabled }: { onAction(): void; disabled?: boolean }) => JSX.Element | null;
  canConfirm?: boolean;
  onConfirm:
    | ((data?: FormData) => void)
    | ((expiry?: number) => void)
    | ((config?: { expiry?: number | undefined } | null) => void);
  onCancel?(): void;
  beastModeRoute?: BeastModePermission;
  confirmation: ConfirmationProps;
  testId?: string;
  copyableValue?: string;
};

export const WithConfirmation = (props: WithConfirmationProps) => {
  const [showConfirmation, setShowConfirmation] = useState(false);

  const handleAction = () => {
    const { beastModeRoute } = props;
    if (beastModeRoute && shouldRefreshBeastMode(beastModeRoute)) {
      redirectToBeastModeEscalation();
      return;
    }
    setShowConfirmation(true);
  };

  const handleConfirm = (data?: FormData & number) => {
    const { onConfirm } = props;
    setShowConfirmation(false);
    onConfirm(data);
  };

  const handleCancel = () => {
    const { onCancel } = props;
    setShowConfirmation(false);
    onCancel && onCancel();
  };

  const { render, canConfirm, confirmation, onConfirm, copyableValue, ...rest } = props;
  const wrappedProps = { ...rest, onAction: handleAction };
  const children = render(wrappedProps);

  return (
    <>
      {children}
      {showConfirmation && (
        <Confirmation
          {...confirmation}
          copyableValue={copyableValue}
          canConfirm={canConfirm}
          onConfirm={handleConfirm}
          onCancel={handleCancel}
        />
      )}
    </>
  );
};
